/* FAQ category filter — the bridge between the two FAQ sections.

   <faq-filter> wraps the pill row in sections/faq-hero.liquid.
   <faq-list>   wraps the accordion in sections/faq-questions.liquid.

   They live in separate sections (Shopify caps a section at 50 blocks, and the
   design comes from two Figma frames), so they talk over document events instead
   of a shared DOM parent: one pill row filters every questions section on the
   page, however many the merchant places, and the pill row can sit above or below
   them in the template order.

   Guarded because this file is loaded by both sections, and once per section
   instance, so it can be evaluated more than once on a page. */
if (!customElements.get('faq-filter')) {
  /* Published by the pill row when the active category changes. */
  const FILTER_EVENT = 'faq:filter';
  /* Published by a question list that joined the page after the pill row, to ask
     for the category currently active. */
  const REQUEST_EVENT = 'faq:filter-request';
  /* The "all" pill carries no key, so an empty key means "show everything". */
  const ALL = '';

  class FaqFilter extends HTMLElement {
    connectedCallback() {
      this.pills = Array.from(this.querySelectorAll('[data-faq-key]'));
      this.currentKey = ALL;

      this.pills.forEach((pill) => {
        pill.addEventListener('click', () => this.select(pill));

        // In the theme editor, activate the pill when its block is selected.
        if (window.Shopify?.designMode) {
          pill.addEventListener('shopify:block:select', () => this.select(pill));
        }
      });

      this.onRequest = () => this.publish(this.currentKey);
      document.addEventListener(REQUEST_EVENT, this.onRequest);

      const active = this.pills.find((pill) => pill.getAttribute('aria-pressed') === 'true');
      this.currentKey = active?.dataset.faqKey || ALL;
    }

    disconnectedCallback() {
      document.removeEventListener(REQUEST_EVENT, this.onRequest);
    }

    select(pill) {
      this.pills.forEach((current) => {
        const selected = current === pill;
        current.classList.toggle('faq-hero__pill--active', selected);
        current.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      this.publish(pill.dataset.faqKey || ALL);
    }

    publish(key) {
      this.currentKey = key || ALL;
      document.dispatchEvent(new CustomEvent(FILTER_EVENT, { detail: { key: this.currentKey } }));
    }
  }

  customElements.define('faq-filter', FaqFilter);

  /* Open/close timing. `<details>` gives no transition of its own, so the panel
     height is animated with Theme.motion (assets/animations.js) — the same helper
     the drawers use. */
  const TOGGLE_DURATION = 0.3;
  const TOGGLE_EASING = [0.39, 0.575, 0.565, 1.0];

  class FaqList extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-faq-categories]'));
      this.empty = this.querySelector('[data-faq-empty]');
      this.singleOpen = this.hasAttribute('data-single-open');
      /* One token per item, bumped on every toggle: an animation that finishes
         after a newer click started must not have the last word on `open`. */
      this.tokens = new WeakMap();

      this.onFilter = (event) => this.apply(event.detail?.key);
      document.addEventListener(FILTER_EVENT, this.onFilter);

      this.items.forEach((item) => {
        const summary = item.querySelector('summary');
        // No answer means no panel to animate — leave the native toggle alone.
        if (!summary || !this.panelOf(item)) return;

        summary.addEventListener('click', (event) => {
          event.preventDefault();
          this.toggle(item);
        });
      });

      if (window.Shopify?.designMode) {
        // A question the active pill filters out still has to show when the
        // merchant selects its block, so clear the filter and open it.
        this.addEventListener('shopify:block:select', (event) => {
          const item = event.target.closest('[data-faq-categories]');
          if (!item) return;
          this.apply(ALL);
          this.closeOthers(item);
          this.setOpen(item, true, false);
        });
      }

      // Ask the pill row which category is active — it may have connected first.
      document.dispatchEvent(new CustomEvent(REQUEST_EVENT));
    }

    disconnectedCallback() {
      document.removeEventListener(FILTER_EVENT, this.onFilter);
    }

    panelOf(item) {
      return item.querySelector('[data-faq-panel]');
    }

    get motionReduced() {
      return window.Theme?.config?.motionReduced === true;
    }

    toggle(item) {
      if (item.open) {
        this.setOpen(item, false);
        return;
      }

      if (this.singleOpen) this.closeOthers(item);
      this.setOpen(item, true);
    }

    closeOthers(item) {
      this.items.forEach((other) => {
        if (other !== item && other.open) this.setOpen(other, false);
      });
    }

    /* Animates the panel between 0 and its natural height. `animate: false` (the
       theme editor, the category filter) switches state with no transition. */
    async setOpen(item, open, animate = true) {
      const panel = this.panelOf(item);
      const token = (this.tokens.get(item) || 0) + 1;
      this.tokens.set(item, token);

      if (!panel || !animate || this.motionReduced || !window.Theme?.motion) {
        this.reset(panel);
        delete item.dataset.faqClosing;
        item.open = open;
        return;
      }

      // Measured before cancelling, so an interrupted animation hands over its
      // current height instead of jumping back to where it started.
      const from = panel.getBoundingClientRect().height;
      panel.getAnimations().forEach((animation) => animation.cancel());

      if (open) {
        delete item.dataset.faqClosing;
        item.open = true;
        panel.style.height = '';
      } else {
        // Marks the card as collapsing so its fill, stroke and caret animate out.
        item.dataset.faqClosing = '';
      }

      const to = open ? panel.getBoundingClientRect().height : 0;

      try {
        await window.Theme.motion.animate(
          panel,
          { height: [`${from}px`, `${to}px`] },
          { duration: TOGGLE_DURATION, easing: TOGGLE_EASING }
        ).finished;
      } catch {
        // Cancelling an animation rejects its finished promise. A newer toggle did
        // the cancelling, and it owns the final state from here.
        return;
      }

      if (this.tokens.get(item) !== token) return;

      this.reset(panel);
      if (!open) {
        item.open = false;
        delete item.dataset.faqClosing;
      }
    }

    /* Theme.motion fills forwards, so the animation has to be dropped for the
       panel to size itself again. */
    reset(panel) {
      if (!panel) return;
      panel.getAnimations().forEach((animation) => animation.cancel());
      panel.style.height = '';
    }

    apply(key) {
      const wanted = (key || ALL).trim();
      let shown = 0;

      this.items.forEach((item) => {
        const categories = (item.dataset.faqCategories || '').split(',').filter(Boolean);
        const matches = wanted === ALL || categories.includes(wanted);

        item.hidden = !matches;
        if (matches) {
          shown += 1;
        } else if (item.open) {
          // Nothing should stay open behind a filter that hides it, and there is
          // nothing to animate on a card that just disappeared.
          this.setOpen(item, false, false);
        }
      });

      if (this.empty) this.empty.hidden = shown > 0;
    }
  }

  customElements.define('faq-list', FaqList);
}
